import React, { Suspense, useState } from 'react'
import { graphql } from 'relay-runtime'
import { useMutation } from 'react-relay'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
} from '@mui/material'
import { toast } from 'react-toastify'
import { OwnerSelect } from './OwnerSelect'
import { FeatureFlagSelector } from '../FeatureFlagSelector'
import { cancelButtonSx } from '../styles'
import { requiredError } from '../validation'
import { AccountFormDialogUpdateMutation } from './__generated__/AccountFormDialogUpdateMutation.graphql'

export interface AdminAccountInput {
  id: string
  name: string
  metaBusinessAccountId: string
  ownerId: string | null
  featureFlagIds: string[]
}

interface AccountFormDialogProps {
  open: boolean
  account: AdminAccountInput | null
  onClose: () => void
  onSaved: () => void
}

const updateAccountMutation = graphql`
  mutation AccountFormDialogUpdateMutation($input: UpdateAccountRequest!) {
    updateAccount(input: $input) {
      id
    }
  }
`

const AccountFormBody = ({
  account,
  onClose,
  onSaved,
}: {
  account: AdminAccountInput
  onClose: () => void
  onSaved: () => void
}) => {
  const [name, setName] = useState(account.name)
  const [metaBusinessAccountId, setMetaBusinessAccountId] = useState(
    account.metaBusinessAccountId
  )
  const [ownerId, setOwnerId] = useState<string | null>(account.ownerId)
  const [featureFlagIds, setFeatureFlagIds] = useState<string[]>(
    account.featureFlagIds
  )

  const [updateAccount, updating] =
    useMutation<AccountFormDialogUpdateMutation>(updateAccountMutation)

  const [submitted, setSubmitted] = useState(false)
  const nameErr = requiredError(name, 'Account name')
  const metaErr = requiredError(metaBusinessAccountId, 'Meta Business Account ID')

  const flagsChanged =
    featureFlagIds.length !== account.featureFlagIds.length ||
    featureFlagIds.some((id) => !account.featureFlagIds.includes(id))
  const dirty =
    name !== account.name ||
    metaBusinessAccountId !== account.metaBusinessAccountId ||
    ownerId !== account.ownerId ||
    flagsChanged

  const handleSubmit = () => {
    setSubmitted(true)
    if (nameErr || metaErr) return
    updateAccount({
      variables: {
        input: {
          id: account.id,
          name,
          metaBusinessAccountId,
          ownerId,
          featureFlagIds,
        },
      },
      onCompleted: () => {
        toast.success('Account updated.')
        onSaved()
        onClose()
      },
      onError: (error) => {
        console.error(error)
        toast.error(error.message || 'Unable to save account.')
      },
    })
  }

  return (
    <>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin="normal"
          label="Account Name"
          value={name}
          onChange={(event) => setName(event.target.value)}
          required
          error={submitted && !!nameErr}
          helperText={submitted ? nameErr ?? undefined : undefined}
        />
        <TextField
          fullWidth
          margin="normal"
          label="Meta Business Account ID"
          value={metaBusinessAccountId}
          onChange={(event) => setMetaBusinessAccountId(event.target.value)}
          required
          error={submitted && !!metaErr}
          helperText={submitted ? metaErr ?? undefined : undefined}
        />
        <Suspense
          fallback={
            <div className="py-2">
              <CircularProgress size={20} />
            </div>
          }
        >
          <OwnerSelect
            accountId={account.id}
            value={ownerId}
            onChange={setOwnerId}
          />
        </Suspense>

        <Divider sx={{ my: 2 }} />

        <Suspense
          fallback={
            <div className="py-2">
              <CircularProgress size={20} />
            </div>
          }
        >
          <FeatureFlagSelector
            value={featureFlagIds}
            onChange={setFeatureFlagIds}
            scope="account"
          />
        </Suspense>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={updating || !dirty}
          startIcon={
            updating ? <CircularProgress size={16} color="inherit" /> : undefined
          }
        >
          Save
        </Button>
        <Button
          variant="contained"
          sx={cancelButtonSx}
          onClick={onClose}
          disabled={updating}
        >
          Cancel
        </Button>
      </DialogActions>
    </>
  )
}

export const AccountFormDialog = ({
  open,
  account,
  onClose,
  onSaved,
}: AccountFormDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      PaperProps={{ sx: { width: '100%', maxWidth: 600 } }}
    >
      <DialogTitle sx={{ color: 'primary.main' }}>Edit Account</DialogTitle>
      {open && account && (
        <AccountFormBody account={account} onClose={onClose} onSaved={onSaved} />
      )}
    </Dialog>
  )
}

export default AccountFormDialog
